import { isCentreSubscription, subscriptionTypes } from '@constants/index';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const THIRTY_DAYS_MS = 30 * ONE_DAY_MS;

interface SubscriptionTimelineItem {
  goalUid?: string;
  startedAt?: string | number | Date;
  expiredAt?: string | number | Date;
  contentTypeId?: number;
  subscription?: {
    type?: number;
    value?: { uid?: string };
  };
}

const getSubscriptionDate = (
  subscription: SubscriptionTimelineItem | null | undefined
) => {
  if (!subscription) return { startedAt: null, expiredAt: null };

  return {
    startedAt: subscription.startedAt ?? null,
    expiredAt: subscription.expiredAt ?? null
  };
};

export const getGoalActiveSubscription = (
  subscriptions: SubscriptionTimelineItem[] | null | undefined,
  goalUID?: string
) => {
  if (!goalUID || !Array.isArray(subscriptions)) return null;

  const activeSubscriptions =
    subscriptions.filter((subscription) => {
      const goalId =
        subscription?.subscription?.value?.uid ?? subscription?.goalUid;
      const { startedAt, expiredAt } = getSubscriptionDate(subscription);

      if (!startedAt || !expiredAt || goalId !== goalUID) return false;

      const start = new Date(startedAt).getTime();
      const end = new Date(expiredAt).getTime();
      const now = Date.now();

      return start <= now && now <= end;
    }) ?? [];

  if (activeSubscriptions.length === 1) return activeSubscriptions[0];

  const plusOrIconicSubscription = activeSubscriptions.find((subscription) => {
    const type = subscription?.subscription?.type;
    const contentTypeId = subscription?.contentTypeId;

    return (
      type === subscriptionTypes.PLUS ||
      type === subscriptionTypes.ICONIC ||
      type === subscriptionTypes.CENTRE ||
      type === subscriptionTypes.ICONIC_PRO ||
      isCentreSubscription(type, contentTypeId)
    );
  });

  if (plusOrIconicSubscription) return plusOrIconicSubscription;

  return activeSubscriptions[0] ?? null;
};

export const isParentProgressFloaterInWindow = (
  startedAt: string | number | Date | null | undefined,
  expiredAt: string | number | Date | null | undefined,
  now = Date.now()
) => {
  if (!startedAt || !expiredAt) return false;

  const start = new Date(startedAt).getTime();
  const expiry = new Date(expiredAt).getTime();

  if (Number.isNaN(start) || Number.isNaN(expiry)) return false;

  const showFrom = start + ONE_DAY_MS;
  const hardStop = Math.min(start + THIRTY_DAYS_MS, expiry);

  return now >= showFrom && now <= hardStop;
};

type GoalInfoWithParentContactPrompt =
  | {
      show_parent_contact_prompt?: boolean;
      showParentContactPrompt?: boolean;
    }
  | null
  | undefined;

const getParentContactPromptValue = (
  goalInfo: GoalInfoWithParentContactPrompt
) => {
  if (!goalInfo) return undefined;

  if (
    Object.prototype.hasOwnProperty.call(goalInfo, 'show_parent_contact_prompt')
  ) {
    return goalInfo.show_parent_contact_prompt;
  }

  if (
    Object.prototype.hasOwnProperty.call(goalInfo, 'showParentContactPrompt')
  ) {
    return goalInfo.showParentContactPrompt;
  }

  return undefined;
};

export const hasShowParentContactPromptKey = (
  goalInfo?: GoalInfoWithParentContactPrompt
) => getParentContactPromptValue(goalInfo) !== undefined;
export const getShowParentContactPrompt = (
  goalInfo?: GoalInfoWithParentContactPrompt
) => getParentContactPromptValue(goalInfo) === true;

interface ManageUpdateBlock {
  meta?: {
    key?: string;
    name?: string;
  };
}

const normalizeParentProgressIdentifier = (value?: string) =>
  value?.toLowerCase().replace(/[^a-z0-9]/g, '') ?? '';

export const isParentProgressManageUpdateBlock = (
  blockType: string,
  block?: ManageUpdateBlock
) => {
  const identifiers = [blockType, block?.meta?.key, block?.meta?.name].map(
    normalizeParentProgressIdentifier
  );

  return identifiers.some(
    (identifier) =>
      identifier === 'parentprogress' ||
      identifier === 'progressupdatesforparents' ||
      (identifier.includes('parent') && identifier.includes('progress'))
  );
};

export const shouldShowParentProgressInSettings = ({
  goalUID,
  goalInfo,
  isGoalInfoLoading,
  isGoalInfoValidating
}: {
  goalUID?: string | null;
  goalInfo?: GoalInfoWithParentContactPrompt;
  isGoalInfoLoading?: boolean;
  isGoalInfoValidating?: boolean;
}) =>
  Boolean(goalUID) &&
  !isGoalInfoLoading &&
  !isGoalInfoValidating &&
  Boolean(goalInfo) &&
  hasShowParentContactPromptKey(goalInfo);
